import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Facility } from '../types';

const FACILITIES_PER_PAGE = 10;

const FacilityManagement: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [facilityToDelete, setFacilityToDelete] = useState<Facility | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFacilities = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const response = await fetch(`${baseUrl}/api/v1/facilities`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch facilities');
      }

      setFacilities(data.data || []);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching facilities.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFacilities();
  }, [token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDelete = async () => {
    if (!facilityToDelete) return;
    try {
      setIsDeleting(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const response = await fetch(`${baseUrl}/api/v1/facilities/${facilityToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete facility');
      }

      setFacilityToDelete(null);
      await fetchFacilities();
    } catch (err: any) {
      alert(err.message || 'An error occurred while deleting the facility.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredFacilities = facilities.filter((f) => {
    const q = searchTerm.toLowerCase();
    return (
      f.name.toLowerCase().includes(q) ||
      f.code.toLowerCase().includes(q) ||
      (f.address || '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredFacilities.length / FACILITIES_PER_PAGE);
  const startIndex = (currentPage - 1) * FACILITIES_PER_PAGE;
  const paginatedFacilities = filteredFacilities.slice(startIndex, startIndex + FACILITIES_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Facility Management</h1>
        <p className="text-sm text-slate-500">Manage production facilities, codes, and addresses</p>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <input
            className="w-full rounded-lg border-slate-200 py-2.5 pl-10 pr-4 text-sm placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]"
            placeholder="Search by Name, Code or Address..."
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-symbols-outlined absolute left-3 top-2.5 text-[20px] text-slate-400">search</span>
        </div>
        <button
          onClick={() => navigate('/facility-management/add')}
          className="flex items-center justify-center gap-2 rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-medium text-white shadow-sm shadow-blue-500/30 transition-all hover:bg-blue-700 whitespace-nowrap"
        >
          <span className="material-symbols-outlined text-[20px]">add</span>
          Add Facility
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-4 border-b border-slate-200">Facility Name</th>
                <th className="px-6 py-4 border-b border-slate-200">Code</th>
                <th className="px-6 py-4 border-b border-slate-200">Address</th>
                <th className="px-6 py-4 border-b border-slate-200 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                      <p>Loading facilities...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedFacilities.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-slate-400">
                      <span className="material-symbols-outlined text-[48px]">factory</span>
                      <p className="text-sm font-medium">
                        {searchTerm ? 'No facilities match your search.' : 'No facilities yet. Add your first one.'}
                      </p>
                      {!searchTerm && (
                        <button
                          onClick={() => navigate('/facility-management/add')}
                          className="mt-1 flex items-center gap-1.5 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                        >
                          <span className="material-symbols-outlined text-[18px]">add</span>
                          Add Facility
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedFacilities.map((facility) => (
                  <tr key={facility.id} className="group transition-colors hover:bg-blue-50/50">
                    <td className="px-6 py-4 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-[#136bce] flex-shrink-0">
                          <span className="material-symbols-outlined text-[20px]">factory</span>
                        </div>
                        <span className="font-medium text-slate-900">{facility.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle">
                      <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-xs font-mono font-medium text-slate-700 ring-1 ring-inset ring-slate-500/10">
                        {facility.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-600 max-w-xs truncate">
                      {facility.address || <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-6 py-4 align-middle">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/user-management/add?facility_id=${facility.id}&role=admin`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-medium text-[#136bce] transition-all hover:bg-blue-100 hover:border-[#136bce]/30"
                        >
                          <span className="material-symbols-outlined text-[16px]">admin_panel_settings</span>
                          Add Admin
                        </button>
                        <button
                          onClick={() => navigate(`/facility-management/edit/${facility.id}`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-[#136bce] hover:border-[#136bce]/30"
                        >
                          <span className="material-symbols-outlined text-[16px]">edit</span>
                          Edit
                        </button>
                        <button
                          onClick={() => setFacilityToDelete(facility)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-100 bg-white px-3 py-1.5 text-xs font-medium text-red-500 transition-all hover:bg-red-50 hover:border-red-200"
                        >
                          <span className="material-symbols-outlined text-[16px]">delete</span>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-sm text-slate-500">
            Showing{' '}
            <span className="font-medium">{filteredFacilities.length > 0 ? startIndex + 1 : 0}</span> to{' '}
            <span className="font-medium">{Math.min(startIndex + FACILITIES_PER_PAGE, filteredFacilities.length)}</span>{' '}
            of <span className="font-medium">{filteredFacilities.length}</span> results
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-[#136bce] text-white shadow-sm ring-1 ring-[#136bce]'
                    : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {facilityToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div
            className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                <span className="material-symbols-outlined text-[32px]">delete</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Facility</h3>
              <p className="text-sm text-slate-500 mb-8">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-slate-700">{facilityToDelete.name}</span>? This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setFacilityToDelete(null)}
                  disabled={isDeleting}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex-1 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Deleting...
                    </div>
                  ) : (
                    'Confirm Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityManagement;
